"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  SyntheticEvent,
} from "react";
import {
  ReactCompareSlider,
  ReactCompareSliderHandle,
  ReactCompareSliderImage,
} from "react-compare-slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export interface ComparisonViewerProps {
  baselineUrl: string;
  targetUrl: string;
  diffUrl?: string;
  summary: {
    overallDisturbanceScore: number;
    layoutDisturbanceScore: number;
    visualDisturbanceScore: number;
    fontDisturbanceScore: number;
  };
  viewport: {
    width: number;
    height: number;
    deviceScaleFactor?: number;
  };
}

type ImageDimensions = { width: number; height: number };
type ComparisonMode = "diff" | "slider";

export function ComparisonViewer({
  baselineUrl,
  targetUrl,
  diffUrl,
  summary,
  viewport,
}: ComparisonViewerProps) {
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>("slider");
  const [sliderWidth, setSliderWidth] = useState(0);
  const sliderContainerRef = useRef<HTMLDivElement | null>(null);
  const sliderResizeObserverRef = useRef<ResizeObserver | null>(null);
  const [baselineDimensions, setBaselineDimensions] =
    useState<ImageDimensions>({ width: 0, height: 0 });
  const [targetDimensions, setTargetDimensions] = useState<ImageDimensions>({
    width: 0,
    height: 0,
  });

  const handleSliderContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (sliderResizeObserverRef.current) {
      sliderResizeObserverRef.current.disconnect();
      sliderResizeObserverRef.current = null;
    }

    sliderContainerRef.current = node;

    if (!node) {
      setSliderWidth(0);
      return;
    }

    const updateWidth = () => {
      setSliderWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    sliderResizeObserverRef.current = new ResizeObserver(updateWidth);
    sliderResizeObserverRef.current.observe(node);
  }, []);

  const computeDisplayedHeight = useCallback(
    (dimensions: ImageDimensions) => {
      if (!dimensions.width || !dimensions.height) {
        return 0;
      }

      if (!sliderWidth) {
        return dimensions.height;
      }

      return Math.ceil((dimensions.height * sliderWidth) / dimensions.width);
    },
    [sliderWidth],
  );

  const baselineDisplayHeight = computeDisplayedHeight(baselineDimensions);
  const targetDisplayHeight = computeDisplayedHeight(targetDimensions);
  const sliderContentHeight = Math.max(
    viewport.height ?? 0,
    baselineDisplayHeight,
    targetDisplayHeight,
    1,
  );

  useEffect(() => {
    setBaselineDimensions({ width: 0, height: 0 });
    setTargetDimensions({ width: 0, height: 0 });
  }, [baselineUrl, targetUrl]);

  const handleBaselineImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setBaselineDimensions({ width: naturalWidth, height: naturalHeight });
    },
    [],
  );

  const handleTargetImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setTargetDimensions({ width: naturalWidth, height: naturalHeight });
    },
    [],
  );

  const formatScore = (score: number) => `${(score * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6 rounded-xl border bg-card p-6 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Visual comparison</h3>
          <p className="text-sm text-muted-foreground">
            {`Viewport ${viewport.width}x${viewport.height}@${viewport.deviceScaleFactor ?? 1}.`}
          </p>
        </div>
        <div className="rounded-lg border p-1">
          <ToggleGroup
            type="single"
            value={comparisonMode}
            onValueChange={(value) => {
              if (value === "diff" || value === "slider") {
                setComparisonMode(value);
              }
            }}
          >
            <ToggleGroupItem
              value="diff"
              disabled={!diffUrl}
              className="data-[state=on]:bg-sidebar-muted data-[state=on]:text-sidebar-primary-foreground"
            >
              Diff overlay
            </ToggleGroupItem>
            <ToggleGroupItem
              value="slider"
              className="data-[state=on]:bg-sidebar-muted data-[state=on]:text-sidebar-primary-foreground"
            >
              Slider
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border bg-muted/60 p-4">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Overall disturbance score
          </p>
          <p className="mt-2 text-xl font-semibold">
            {formatScore(summary.overallDisturbanceScore)}
          </p>
        </div>
        {/* <div className="rounded-lg border bg-muted/60 p-4">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Visual disturbance
          </p>
          <p className="mt-2 text-xl font-semibold">
            {formatScore(summary.visualDisturbanceScore)}
          </p>
        </div>
        <div className="rounded-lg border bg-muted/60 p-4">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Font disturbance
          </p>
          <p className="mt-2 text-xl font-semibold">
            {formatScore(summary.fontDisturbanceScore)}
          </p>
        </div>
        <div className="rounded-lg border bg-muted/60 p-4">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Layout disturbance
          </p>
          <p className="mt-2 text-xl font-semibold">
            {formatScore(summary.layoutDisturbanceScore)}
          </p>
        </div> */}
      </div>

      <p className="text-xs text-muted-foreground">
        Lower scores indicate closer visual parity. Follow the docs
        recommendation by using visual + font disturbance as your primary
        acceptance gate.
      </p>

      <div
        ref={handleSliderContainerRef}
        className="relative w-full overflow-auto rounded-lg border bg-muted"
      >
        {comparisonMode === "diff" && diffUrl ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={diffUrl}
              alt="Diff screenshot"
              className="block w-full"
            />
          </>
        ) : (
          <ReactCompareSlider
            className="w-full"
            style={{
              width: "100%",
              minHeight: `${sliderContentHeight}px`,
              height: `${sliderContentHeight}px`,
            }}
            boundsPadding={0}
            handle={<ReactCompareSliderHandle />}
            itemOne={
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  minHeight: "100%",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  backgroundColor: "hsl(var(--muted))",
                }}
              >
                <ReactCompareSliderImage
                  src={baselineUrl}
                  alt="Baseline screenshot"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    backgroundColor: "transparent",
                  }}
                  onLoad={handleBaselineImageLoad}
                />
              </div>
            }
            itemTwo={
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  minHeight: `100%`,
                  justifyContent: "center",
                  alignItems: "flex-start",
                  backgroundColor: "hsl(var(--muted))",
                }}
              >
                <ReactCompareSliderImage
                  src={targetUrl}
                  alt="Target screenshot"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    backgroundColor: "transparent",
                  }}
                  onLoad={handleTargetImageLoad}
                />
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
